import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import extend from "backbone-extend-standalone";
var INITIALIZED = "initialized",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  AUTHENTICATED = "authenticated";

/**
 * Creates a new Session instance.
 * @param {Object} [options] An options hash.
 * @param {Integer} [options.refreshInterval:10000] The interval between periodic checks of session state, in milliseconds.
 * @param {Integer} [options.timeoutInterval:30000] The number of seconds to wait before assuming a server request has timed out.
 * @constructor
 */
function Session(options) {
  var self = this;

  this.options = _.defaults({}, options, {
    refreshInterval: 10000,
    timeoutInterval: 30000,
  });

  _.extend(this, Backbone.Events);

  Object.defineProperty(self, "state", {
    get: function () {
      return self._state;
    },
  });

  this.initialize(this.options);

  setInterval(function () {
    //Only query the state if you are not already disconnected, The login screen function will set the state back to connected
    if (self._state != DISCONNECTED) {
      self.queryState();
    }
  }, this.options.refreshInterval);
}

Session.prototype = {
  _state: INITIALIZED,

  /**
   * Initializes the session object.
   * @param {Object} [options] The options hash sent to the constructor.
   * @param {Integer} [options.refreshInterval:10000] The interval between periodic checks of session state, in milliseconds.
   * @param {Integer} [options.timeoutInterval:30000] The number of seconds to wait before assuming a server request has timed out.
   */
  initialize: function (options) {},

  /**
   * Gets a value indicating whether or not the session is authenticated.
   * @return {boolean} True if the session is *not* authenticated, otherwise false.
   */
  isAnonymous: function () {
    return this.state != AUTHENTICATED;
  },

  /**
   * Performs the given operation when the session is authenticated for the first time.
   * @param {Function} callback The operation to perform.
   */
  whenAuthenticated: function (callback) {
    if (this.isAnonymous()) {
      this.once("authenticate:success", callback);
    } else {
      callback();
    }
  },

  /**
   * Gets a string array indicating the roles present for the current session.
   * @return {User.attributes.Permissions|{collection, via}} The array of roles.
   * @abstract
   */
  getRoles: function () {
    return null;
  },

  /**
   * Determines whether the user associated with the session is in a specified role.
   * @param  {string|Array}  role The role name or names in question.
   * @return {Boolean}           True if the user is in all provided roles, otherwise false.
   * @abstract
   */
  hasRole: function (role) {
    return false;
  },

  /**
   * Determines whether the user associated with the session is in any specified role.
   * @param  {string|Array}  role The role name or names in question.
   * @return {Boolean}           True if the user is in any provided roles, otherwise false.
   * @abstract
   */
  hasAnyRole: function (role) {
    return false;
  },

  //determine if the current user can perform the specified action on the given type
  //returns true if they can, otherwise false.
  /**
   * Gets a value indicating whether or not the user associated with the current session can perform the specified action on the given type.
   * @param {String|Array} action The action or array of actions to check.
   * @param {String|Object} type A string representing the type to query on, or a hash where the singular key is a type and its value is a model or id to query on. Pass null for global.
   * @return {Boolean} True if the user can perform any of the provided actions on the given type, otherwise false.
   * @abstract
   */
  can: function (action, type) {
    return false;
  },

  /**
   * Gets the default login url.
   * @protected
   * @abstract
   * @return {String} The default login URL.
   */
  getAjaxLoginUrl: function () {
    return "";
  },

  /**
   * Gets the default logout url.
   * @protected
   * @abstract
   * @return {String} The default logout URL.
   */
  getAjaxLogoutUrl: function () {
    return "";
  },

  /**
   * Executes an AJAX login request. The URL is posted the credentials as JSON with "username" and "password" key.
   * @param  {string} username The user name to use for the request.
   * @param  {string} password The password to use for the request.
   * @param  {object} [options]  An options hash.
   * @param {Boolean} options.trigger:true `true` if events shall be raised by this invocation, otherwise `false`.
   * @param {String} [options.url] The login URL to use. Default is `UrlService.apiurl('/auth/logOn')`.
   * @return {jqXHR}          An object representing the request.
   */
  login: function (username, password, options) {
    var url = (options && options.url) || this.getAjaxLoginUrl();
    var data = {
      username: username,
      password: password,
    };
    var self = this;

    return $.ajax({
      type: "POST",
      url: url,
      data: JSON.stringify(data),
      dataType: "json",
      contentType: "application/json",
      timeout: this.options.timeoutInterval,
    }).then(
      function (data) {
        if (data.error) {
          self._setState(
            CONNECTED,
            _.defaults({}, options, {
              error: {
                error: data.error,
              },
            })
          );
        } else {
          self.trigger("login", data, options);

          return self.queryState(options);
        }
      },
      function (jqXHR) {
        if (jqXHR.status == 302) {
          return self.queryState(options);
        } else {
          self._responseError(jqXHR, options);
        }
      }
    );
  },

  /**
   * Executes an AJAX logout request. The URL receives a GET request.
   * @param  {object} [options] An options hash.
   * @param {Boolean} options.trigger:true `true` if events shall be raised by this invocation, otherwise `false`.
   * @param {String} [options.url] The logout URL to use. Default is `UrlService.apiurl('/auth/logOff')`.
   * @return {jqXHR}         An object representing the request.
   */
  logout: function (options) {
    var url = (options && options.url) || this.getAjaxLogoutUrl();
    var self = this;

    return $.ajax({
      type: "GET",
      url: url,
      timeout: this.options.timeoutInterval,
      success: function () {
        self._setState(CONNECTED, options);
      },
      error: function (jqXHR) {
        self._responseError(jqXHR, options);
      },
    });
  },

  /**
   * Executes the actual query request.
   * @param {object} options An options hash. This is the hash passed to `queryState`.
   * @protected
   * @abstract
   * @return {Promise} A promise which rejects with an error and any options or resolves with the new session state and any options.
   */
  doQuery: function (options) {
    return $.when(Session.INITIALIZED);
  },

  /**
   * Executes an AJAX request which reaffirms the current `state` of the user. Internally, this falls `fetch`.
   * @param  {object} [options] An options hash.
   * @param {Boolean} [options.trigger:true] `true` if events shall be raised by this invocation, otherwise `false`.
   * @return {Promise} A promise which rejects with an error and any options or resolves with the new session state and any options.
   */
  queryState: function (options) {
    var self = this;

    return this.doQuery(options).then(
      function (state, options) {
        self._setState(state, options);
      },
      function (err, options) {
        self._responseError(err, options);
      }
    );
  },

  /**
   * Gets the session state associated with the given error.
   * @param err An error object. The first argument to failure callbacks of `queryState`.
   * @param [options] An options hash.
   * @protected
   */
  getErrorState: function (err, options) {
    var state = Session.CONNECTED;

    if (err.statusText == "timeout" || err.readyState === 0) {
      state = Session.DISCONNECTED;
    }

    return state;
  },

  /**
   * Gets the error message associated with the given error.
   * @param err An error object. The first argument to failure callbacks of `queryState`.
   * @param [options] An options hash.
   * @protected
   */
  getErrorText: function (err, options) {
    var message = err.statusText;

    if (message == "timeout") {
      message = "Connection timeout.";
    }

    if (message == "error") {
      message = null;
    }

    return message;
  },

  /**
   * Examines the given request for error conditions and changes the `state` as necessary.
   * @param  err   The error object.
   * @param  {object} [options] An options hash.
   * @param {Boolean} [options.trigger:true] `true` if events shall be raised by this invocation, otherwise `false`.
   * @return         An object representing the error.
   */
  _responseError: function (err, options) {
    this._setState(
      this.getErrorState(err, options),
      _.defaults({}, options, {
        error: {
          error: this.getErrorText(err, options),
        },
      })
    );

    return err;
  },

  /**
   * Sets the current state of the current user.
   * @private
   * @param {string} newState The new state. One of `CurrentUser.INITIALIZED`, `CurrentUser.CONNECTED`, `CurrentUser.DISCONNECTED`, `CurrentUser.AUTHENTICATED`.
   * @param {object} [options]    An options hash.
   * @param {Object} [options.error] An object containing an `error` field with text describing the error that occurred during the transition, if any.
   * @param {Boolean} [options.trigger] `true` to trigger events on the transition. Otherwise `false`.
   */
  _setState: function (newState, options) {
    var oldState = this.state;
    var changeEvent, errorEvent;

    options = _.defaults({}, options, {
      error: false,
      trigger: true,
    });

    switch (newState) {
      case CONNECTED:
        changeEvent = "connect";
        errorEvent = "authenticate:error";
        break;

      case DISCONNECTED:
        changeEvent = "disconnect";
        errorEvent = "connect:error";
        break;

      case AUTHENTICATED:
        changeEvent = "authenticate:success";
        break;
    }

    this._state = newState;

    if (options.trigger) {
      if (options.error && errorEvent) {
        this.trigger(errorEvent, options.error);
      }

      if (newState != oldState) {
        this.trigger(changeEvent);
      }
    }
  },
};

_.extend(Session, {
  /**
   * A constant representing the "CONNECTED" state of the current user. Used when a route to the server exists, but the session is not authenticated. The current user can transition from CONNECTED to either DISCONNECTED or AUTHENTICATED.
   * @type {string}
   */
  CONNECTED: CONNECTED,
  /**
   * A constant representing the "AUTHENTICATED" state of the current user. Used when a route to the server exists and the session is authenticated. The current user can transition from AUTHENTICATED to either DISCONNECTED or CONNECTED.
   * @type {string}
   */
  AUTHENTICATED: AUTHENTICATED,
  /**
   * A constant representing the "DISCONNECTED" state of the current user. Used when a route to the server does not exist. The current user can transition from DISCONNECTED to either CONNECTED or AUTHENTICATED.
   * @type {string}
   */
  DISCONNECTED: DISCONNECTED,
  /**
   * A constant representing the "INITIALIZED" state of the current user. Used when the object is initially created. The current user can transition from INITIALIZED to any other state.
   * @type {string}
   */
  INITIALIZED: INITIALIZED,
});

Session.extend = extend;

export default Session;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/base/Session.js
// module id = 864
// module chunks = 1